// ══════════════════════════════════════════════════════════
// CHECKOUT RETURN — Post-payment return handler
//
// Se monta cuando el usuario regresa del procesador de pagos
// a /admin/settings?checkout=success o ?checkout=cancelled.
//
// Reglas de seguridad:
// - NUNCA asume PAID por regresar a success_url.
// - NUNCA activa plan, suscripción ni estación localmente.
// - NUNCA escribe payment_status ni subscription_status.
// - Solo consulta el backend (read-only polling).
// - checkout_id proviene de sessionStorage (guardado antes del redirect).
//
// Para RFW_MANAGED: PAID no marca estación como ACTIVE.
// Continúa a PENDING_PROVISION / PENDING_APPROVAL.
// ══════════════════════════════════════════════════════════

import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { getAndClearPendingCheckoutId, getCheckoutStatus, isCheckoutTerminal, isCheckoutPaid } from '@/services/checkout';
import type { CheckoutStatusResponse } from '@/types/checkout';

// ── CONSTANTS ──

const POLL_INTERVAL_MS = 5000; // 5 segundos mínimo
const MAX_POLL_ATTEMPTS = 24;   // 24 × 5s = 2 minutos máximo
const TERMINAL_STATUSES = ['PAID', 'FAILED', 'CANCELLED', 'EXPIRED'];

// ── STATUS DISPLAY ──

interface StatusDisplay {
  icon: string;
  iconBg: string;
  iconColor: string;
  title: string;
  description: string;
  badge: string;
  badgeBg: string;
  badgeText: string;
}

function getStatusDisplay(status: string, streamingMode: string | null): StatusDisplay {
  switch (status) {
    case 'PENDING':
      return {
        icon: 'ri-hourglass-line',
        iconBg: 'bg-amber-100',
        iconColor: 'text-amber-600',
        title: 'Pago pendiente',
        description: 'Tu pago está pendiente de confirmación. Esto puede tardar unos minutos.',
        badge: 'Pendiente',
        badgeBg: 'bg-amber-50 border-amber-200',
        badgeText: 'text-amber-700',
      };
    case 'REQUIRES_PAYMENT':
      return {
        icon: 'ri-bank-card-line',
        iconBg: 'bg-amber-100',
        iconColor: 'text-amber-600',
        title: 'Falta completar el pago',
        description: 'El pago no se ha completado. Volvé a intentarlo para continuar.',
        badge: 'Requiere pago',
        badgeBg: 'bg-amber-50 border-amber-200',
        badgeText: 'text-amber-700',
      };
    case 'PROCESSING':
      return {
        icon: 'ri-loader-4-line',
        iconBg: 'bg-sky-100',
        iconColor: 'text-sky-600 animate-spin',
        title: 'Verificando tu pago',
        description: 'El pago está siendo procesado. Esto puede tomar unos segundos...',
        badge: 'Verificando',
        badgeBg: 'bg-sky-50 border-sky-200',
        badgeText: 'text-sky-700',
      };
    case 'PAID': {
      const isManaged = streamingMode === 'RFW_MANAGED';
      return {
        icon: 'ri-check-double-line',
        iconBg: 'bg-emerald-100',
        iconColor: 'text-emerald-600',
        title: '¡Pago confirmado!',
        description: isManaged
          ? 'Tu pago fue confirmado. RadioFrecuenciaWeb está preparando tu estación de streaming. Recibirás un correo cuando esté lista.'
          : 'Tu pago fue confirmado. Tu plan premium ya está activo.',
        badge: isManaged ? 'Aprovisionamiento pendiente' : 'Confirmado',
        badgeBg: isManaged ? 'bg-amber-50 border-amber-200' : 'bg-emerald-50 border-emerald-200',
        badgeText: isManaged ? 'text-amber-700' : 'text-emerald-700',
      };
    }
    case 'FAILED':
      return {
        icon: 'ri-close-circle-line',
        iconBg: 'bg-red-100',
        iconColor: 'text-red-600',
        title: 'El pago no pudo procesarse',
        description: 'Hubo un problema con tu pago. Intentá nuevamente o usá otro método de pago.',
        badge: 'Fallido',
        badgeBg: 'bg-red-50 border-red-200',
        badgeText: 'text-red-700',
      };
    case 'CANCELLED':
      return {
        icon: 'ri-close-circle-line',
        iconBg: 'bg-foreground-100',
        iconColor: 'text-foreground-500',
        title: 'Pago cancelado',
        description: 'Cancelaste el pago. Podés intentarlo de nuevo cuando quieras.',
        badge: 'Cancelado',
        badgeBg: 'bg-foreground-100 border-foreground-200',
        badgeText: 'text-foreground-600',
      };
    case 'EXPIRED':
      return {
        icon: 'ri-timer-line',
        iconBg: 'bg-foreground-100',
        iconColor: 'text-foreground-500',
        title: 'El checkout expiró',
        description: 'El tiempo para completar el pago venció. Creá un nuevo checkout para continuar.',
        badge: 'Expirado',
        badgeBg: 'bg-foreground-100 border-foreground-200',
        badgeText: 'text-foreground-600',
      };
    default:
      return {
        icon: 'ri-time-line',
        iconBg: 'bg-foreground-100',
        iconColor: 'text-foreground-500',
        title: 'Verificando estado...',
        description: 'Consultando el estado de tu pago.',
        badge: status,
        badgeBg: 'bg-foreground-100 border-foreground-200',
        badgeText: 'text-foreground-600',
      };
  }
}

// ── COMPONENT ──

export default function CheckoutReturn() {
  const [checkoutStatus, setCheckoutStatus] = useState<CheckoutStatusResponse | null>(null);
  const [pollingStatus, setPollingStatus] = useState<string>('PROCESSING');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [attempts, setAttempts] = useState(0);
  const [streamingMode, setStreamingMode] = useState<string | null>(null);

  // ── Polling function ──
  const poll = useCallback(async (checkoutId: string) => {
    try {
      const status = await getCheckoutStatus(checkoutId);
      setCheckoutStatus(status);
      setPollingStatus(status.status);
      setAttempts((prev) => prev + 1);

      if (isCheckoutTerminal(status.status)) {
        setLoading(false);
        return;
      }

      // Keep polling
      setTimeout(() => {
        poll(checkoutId);
      }, POLL_INTERVAL_MS);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al consultar el estado del pago.');
      setLoading(false);
    }
  }, []);

  // ── Initialize on mount ──
  useEffect(() => {
    const stored = getAndClearPendingCheckoutId();

    if (!stored) {
      // No checkout pending — maybe user navigated here directly with checkout=success
      // We show a generic state
      setLoading(false);
      setPollingStatus('UNKNOWN');
      return;
    }

    setStreamingMode(stored.streamingMode);

    // Start polling
    let cancelled = false;
    let currentAttempts = 0;
    let timer: ReturnType<typeof setTimeout> | null = null;

    const doPoll = async () => {
      if (cancelled) return;

      try {
        const status = await getCheckoutStatus(stored.checkoutId);
        if (cancelled) return;

        setCheckoutStatus(status);
        setPollingStatus(status.status);
        currentAttempts += 1;
        setAttempts(currentAttempts);

        if (isCheckoutTerminal(status.status)) {
          setLoading(false);
          return;
        }

        if (currentAttempts >= MAX_POLL_ATTEMPTS) {
          setLoading(false);
          setError('El checkout excedió el tiempo máximo de espera. Verificá tu suscripción manualmente desde la pestaña Suscripción.');
          return;
        }

        timer = setTimeout(doPoll, POLL_INTERVAL_MS);
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Error al consultar el estado del pago.');
          setLoading(false);
        }
      }
    };

    doPoll();

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, []);

  // ── Manual refresh ──
  const handleRefresh = useCallback(async () => {
    const storedId = sessionStorage.getItem('rfw_pending_checkout_id');
    if (!storedId) {
      setError('No se encontró un checkout pendiente. Si ya pagaste, verificá tu suscripción en la pestaña Suscripción.');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const status = await getCheckoutStatus(storedId);
      setCheckoutStatus(status);
      setPollingStatus(status.status);
      setLoading(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al consultar el estado.');
      setLoading(false);
    }
  }, []);

  // ── Handle close (user dismisses) ──
  const handleClose = useCallback(() => {
    // Clear pending state
    if (typeof window !== 'undefined') {
      try {
        sessionStorage.removeItem('rfw_pending_checkout_id');
        sessionStorage.removeItem('rfw_pending_streaming_mode');
      } catch { /* noop */ }
    }
    // Navigate to subscription tab instead
    window.location.search = '';
    // Reload to show subscription tab without checkout overlay
    window.location.reload();
  }, []);

  // ── Derived state ──
  const isTerminal = pollingStatus ? TERMINAL_STATUSES.includes(pollingStatus) : false;
  const isPaid = isCheckoutPaid(pollingStatus || '');
  const isManaged = streamingMode === 'RFW_MANAGED';
  const isFailed = pollingStatus === 'FAILED';
  const isCancelled = pollingStatus === 'CANCELLED';
  const isExpired = pollingStatus === 'EXPIRED';
  const isProcessing = pollingStatus === 'PROCESSING' || pollingStatus === 'PENDING' || pollingStatus === 'REQUIRES_PAYMENT';
  const statusDisplay = getStatusDisplay(pollingStatus, streamingMode);

  // ── RENDER ──

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-sm p-4">
      {/* Overlay click to close (only when terminal) */}
      {isTerminal && (
        <div
          className="absolute inset-0 cursor-pointer"
          onClick={handleClose}
        ></div>
      )}

      <div className="relative w-full max-w-md bg-background-50 rounded-2xl border border-background-200 shadow-2xl overflow-hidden">
        {/* Close button */}
        {isTerminal && (
          <button
            onClick={handleClose}
            className="absolute top-3 right-3 w-8 h-8 flex items-center justify-center rounded-lg bg-background-100 hover:bg-background-200 text-foreground-400 hover:text-foreground-600 transition-colors cursor-pointer z-10"
          >
            <i className="ri-close-line text-sm"></i>
          </button>
        )}

        <div className="p-8 text-center">
          {/* Icon */}
          <div className={`w-16 h-16 rounded-2xl ${statusDisplay.iconBg} flex items-center justify-center mx-auto mb-5`}>
            <i className={`${statusDisplay.icon} ${statusDisplay.iconColor} text-2xl`}></i>
          </div>

          {/* Badge */}
          <div className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full ${statusDisplay.badgeBg} border ${statusDisplay.badgeText} text-[11px] font-semibold mb-3`}>
            <span className={`w-1.5 h-1.5 rounded-full ${isProcessing ? 'animate-pulse' : ''} ${
              isPaid ? 'bg-emerald-500' :
              isFailed ? 'bg-red-500' :
              isProcessing ? 'bg-sky-500' :
              'bg-foreground-400'
            }`}></span>
            {statusDisplay.badge}
          </div>

          {/* Title */}
          <h2 className="text-lg font-bold text-foreground-950 mb-2">
            {statusDisplay.title}
          </h2>

          {/* Description */}
          <p className="text-foreground-500 text-sm leading-relaxed mb-6">
            {statusDisplay.description}
          </p>

          {/* Polling progress bar */}
          {isProcessing && !error && (
            <div className="mb-6">
              <div className="w-full bg-background-100 rounded-full h-1.5 overflow-hidden">
                <div
                  className="h-full bg-sky-400 rounded-full transition-all duration-1000"
                  style={{ width: `${Math.min((attempts / MAX_POLL_ATTEMPTS) * 100, 100)}%` }}
                ></div>
              </div>
              <p className="text-foreground-400 text-[10px] mt-2">
                Intento {attempts} de {MAX_POLL_ATTEMPTS} · {pollingStatus === 'PROCESSING' ? 'Procesando pago...' : 'Esperando confirmación...'}
              </p>
            </div>
          )}

          {/* Error message */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-3 mb-6 text-left">
              <p className="text-red-700 text-xs font-semibold mb-1 flex items-center gap-1.5">
                <i className="ri-error-warning-line"></i>
                Error
              </p>
              <p className="text-red-600 text-[11px]">{error}</p>
            </div>
          )}

          {/* Managed streaming post-payment notice */}
          {isPaid && isManaged && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6 text-left">
              <p className="text-amber-800 text-xs font-semibold mb-2 flex items-center gap-1.5">
                <i className="ri-cloud-line"></i>
                Aprovisionamiento de Streaming
              </p>
              <div className="space-y-1.5">
                {[
                  { icon: 'ri-check-double-line', text: 'Pago confirmado' },
                  { icon: 'ri-server-line', text: 'RFW prepara tu servidor de streaming' },
                  { icon: 'ri-disc-line', text: 'Configuración de AutoDJ y almacenamiento' },
                  { icon: 'ri-mail-line', text: 'Recibirás tu Stream URL por correo' },
                ].map((step, i) => (
                  <div key={i} className="flex items-start gap-2">
                    <div className="w-5 h-5 rounded-full bg-amber-200 flex items-center justify-center shrink-0 mt-0.5">
                      <i className={`${step.icon} text-amber-700 text-[9px]`}></i>
                    </div>
                    <span className="text-[11px] text-amber-800">{step.text}</span>
                  </div>
                ))}
              </div>
              <p className="text-amber-600 text-[10px] mt-3 leading-relaxed">
                Tu estación se activará automáticamente cuando el aprovisionamiento esté completo. No se requiere ninguna acción adicional de tu parte.
              </p>
            </div>
          )}

          {/* Actions */}
          <div className="space-y-2.5">
            {isPaid && (
              <>
                <Link
                  to="/admin/dashboard"
                  className="block w-full py-2.5 rounded-xl bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-bold text-center transition-colors cursor-pointer whitespace-nowrap"
                >
                  {isManaged ? 'Ir al panel — Ver aprovisionamiento' : 'Ir al panel de administración'}
                  <i className="ri-arrow-right-line ml-2"></i>
                </Link>
                <button
                  onClick={handleClose}
                  className="block w-full py-2.5 rounded-xl bg-background-100 hover:bg-background-200 text-foreground-600 text-sm font-semibold text-center transition-colors cursor-pointer whitespace-nowrap"
                >
                  Quedarme en Configuración
                </button>
              </>
            )}

            {(isFailed || isExpired) && (
              <>
                <Link
                  to="/pricing"
                  className="block w-full py-2.5 rounded-xl bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-bold text-center transition-colors cursor-pointer whitespace-nowrap"
                >
                  {isExpired ? 'Crear nuevo checkout' : 'Reintentar pago'}
                  <i className="ri-arrow-right-line ml-2"></i>
                </Link>
                <button
                  onClick={handleRefresh}
                  className="block w-full py-2.5 rounded-xl bg-background-100 hover:bg-background-200 text-foreground-600 text-sm font-semibold text-center transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-refresh-line mr-1.5"></i>
                  Actualizar estado
                </button>
              </>
            )}

            {isCancelled && (
              <>
                <Link
                  to="/pricing"
                  className="block w-full py-2.5 rounded-xl bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-bold text-center transition-colors cursor-pointer whitespace-nowrap"
                >
                  Ver planes
                  <i className="ri-arrow-right-line ml-2"></i>
                </Link>
                <button
                  onClick={handleClose}
                  className="block w-full py-2.5 rounded-xl bg-background-100 hover:bg-background-200 text-foreground-600 text-sm font-semibold text-center transition-colors cursor-pointer whitespace-nowrap"
                >
                  Cerrar
                </button>
              </>
            )}

            {isProcessing && !error && (
              <div className="text-center">
                <p className="text-foreground-400 text-[11px] mb-2">
                  Esto puede tardar hasta 2 minutos. No cierres esta ventana.
                </p>
                <button
                  onClick={handleRefresh}
                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-background-100 hover:bg-background-200 text-foreground-500 text-xs font-semibold transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-refresh-line"></i>
                  Actualizar manualmente
                </button>
              </div>
            )}

            {error && (
              <>
                <button
                  onClick={handleRefresh}
                  className="block w-full py-2.5 rounded-xl bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-bold text-center transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-refresh-line mr-1.5"></i>
                  Reintentar
                </button>
                <button
                  onClick={handleClose}
                  className="block w-full py-2.5 rounded-xl bg-background-100 hover:bg-background-200 text-foreground-600 text-sm font-semibold text-center transition-colors cursor-pointer whitespace-nowrap"
                >
                  Cerrar — verificar después
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}